import React, { useState, useEffect, useCallback } from 'react';
import { useWordContext } from '../context/WordContext';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import '../styles/SavedWordsFlashcards.css';

/**
 * Format an interval in days into a human-readable string.
 * Mirrors the server-side formatInterval() in savedWordSrsController.js.
 */
const formatInterval = (days) => {
    if (!days || days <= 0) return 'New';
    if (days < 1) {
        const minutes = Math.round(days * 24 * 60);
        if (minutes < 60) return `${minutes} min`;
        return `${Math.round(minutes / 60)}h ${minutes % 60}m`;
    }
    if (days < 30) return `${Math.round(days)} day${Math.round(days) !== 1 ? 's' : ''}`;
    if (days < 365) return `${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? 's' : ''}`;
    return `${(days / 365).toFixed(1)} year${(days / 365).toFixed(1) !== '1.0' ? 's' : ''}`;
};

const SavedWordsFlashcards = () => {
    const { savedWords, refreshSavedWords } = useWordContext();

    // User info
    const [userId, setUserId] = useState(null);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        reviewedCount: 0,
        notReviewedCount: 0,
        totalDue: 0,
        newCardsTotal: 0,
        reviewCardsDue: 0
    });
    const [statsLoading, setStatsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Session state
    const [sessionActive, setSessionActive] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(false);

    // SRS settings (like Anki's deck options)
    const [newCardsPerDay, setNewCardsPerDay] = useState(10);
    const [maxReviewsPerDay, setMaxReviewsPerDay] = useState(50);
    const [newCardsIgnoreReviewLimit, setNewCardsIgnoreReviewLimit] = useState(true);

    // Queue state
    const [queue, setQueue] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [revealed, setRevealed] = useState(false);

    // Session summary
    const [sessionRatings, setSessionRatings] = useState({
        again: 0,
        hard: 0,
        good: 0,
        easy: 0
    });
    const [sessionComplete, setSessionComplete] = useState(false);

    // Helper to get userId
    const getUserId = useCallback(() => {
        // Try JWT token first
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
                    '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                ).join(''));
                const decoded = JSON.parse(jsonPayload);
                if (decoded.sub) {
                    localStorage.setItem('userId', decoded.sub);
                    return decoded.sub;
                }
            } catch (e) {
                logger.error('SavedWordsFlashcards - Error decoding token:', e);
            }
        }
        return localStorage.getItem('userId') || null;
    }, []);

    // Get the current language from settings
    const getLanguage = useCallback(() => {
        return localStorage.getItem('learningLanguage') || 'Spanish';
    }, []);

    // Fetch stats on mount and when language changes
    const fetchStats = useCallback(async () => {
        const uid = getUserId();
        const lang = getLanguage();

        if (!uid) {
            setStatsLoading(false);
            return;
        }

        try {
            setStatsLoading(true);
            const response = await apiClient.get(`/api/saved-words/stats?userId=${uid}&language=${lang}`);
            const data = await response.json();

            if (data.success && data.stats) {
                setStats(data.stats);
            }
        } catch (err) {
            logger.error('SavedWordsFlashcards - Error fetching stats:', err);
            setError('Failed to load stats. Please try again.');
        } finally {
            setStatsLoading(false);
        }
    }, [getUserId, getLanguage]);

    useEffect(() => {
        const uid = getUserId();
        setUserId(uid);
        if (uid) {
            fetchStats();
        }
    }, [fetchStats, getUserId]);

    // Listen for language changes
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'learningLanguage') {
                fetchStats();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [fetchStats]);


    // Start a review session
    const startSession = async () => {
        const uid = getUserId();
        const lang = getLanguage();

        if (!uid) {
            setError('User not identified. Please log in again.');
            return;
        }

        setSessionLoading(true);
        setError(null);
        setSessionComplete(false);
        setSessionRatings({ again: 0, hard: 0, good: 0, easy: 0 });

        try {
            // Pass SRS settings as query params
            const params = new URLSearchParams({
                userId: uid,
                language: lang,
                newCardsPerDay: String(newCardsPerDay),
                maxReviewsPerDay: String(maxReviewsPerDay),
                newCardsIgnoreReviewLimit: String(newCardsIgnoreReviewLimit)
            });
            const response = await apiClient.get(
                `/api/saved-words/due?${params.toString()}`
            );
            const data = await response.json();

            if (data.success && data.dueWords && data.dueWords.length > 0) {
                setQueue(data.dueWords);
                setCurrentIndex(0);
                setRevealed(false);
                setSessionActive(true);
                setStats(data.stats);
            } else {
                setError('No words due for review! All caught up.');
                setSessionActive(false);
            }
        } catch (err) {
            logger.error('SavedWordsFlashcards - Error starting session:', err);
            setError('Failed to start session. Please try again.');
        } finally {
            setSessionLoading(false);
        }
    };

    // Handle card reveal (show definition) — no API call needed, data is already saved
    const handleReveal = () => {
        if (revealed) return;
        setRevealed(true);
    };

    // Handle rating a card
    const handleRating = async (rating) => {
        const currentWord = queue[currentIndex];
        if (!currentWord) return;

        const uid = getUserId();
        const lang = currentWord.language || getLanguage();

        try {
            await apiClient.post('/api/saved-words/review', {
                userId: uid,
                language: lang,
                item: currentWord.item,
                rating
            });

            // Track rating for summary
            setSessionRatings(prev => ({
                ...prev,
                [rating]: prev[rating] + 1
            }));

            // Handle "again" - move card to end of queue
            if (rating === 'again') {
                const updatedQueue = [...queue];
                const card = updatedQueue.splice(currentIndex, 1)[0];
                updatedQueue.push(card);
                setQueue(updatedQueue);
                // Don't advance index since we removed the current card
                setRevealed(false);
                return;
            }

            // Normal: advance to next card
            const nextIndex = currentIndex + 1;
            if (nextIndex < queue.length) {
                setCurrentIndex(nextIndex);
                setRevealed(false);
            } else {
                // Session complete
                setSessionComplete(true);
                // Refresh stats
                fetchStats();
                // Refresh saved words from context
                refreshSavedWords(lang);
            }
            } catch (err) {
                logger.error('SavedWordsFlashcards - Error submitting rating:', err);
                setError('Failed to save rating. Please try again.');
            }
        };
    
        // Handle "Remove Word" — marks word as known (removes highlight from text) but keeps it saved
        const handleRemoveWord = async () => {
            const currentWord = queue[currentIndex];
            if (!currentWord) return;
    
            const uid = getUserId();
            const lang = currentWord.language || getLanguage();
    
            try {
                await apiClient.post('/api/updateWordKnown', {
                    userId: uid,
                    language: lang,
                    item: currentWord.item,
                    known: true
                });
    
                // Advance to next card (same logic as a successful rating)
                const nextIndex = currentIndex + 1;
                if (nextIndex < queue.length) {
                    setCurrentIndex(nextIndex);
                    setRevealed(false);
                } else {
                    // Session complete
                    setSessionComplete(true);
                    fetchStats();
                    refreshSavedWords(lang);
                }
            } catch (err) {
                logger.error('SavedWordsFlashcards - Error removing word:', err);
                setError('Failed to remove word. Please try again.');
            }
        };
    
        // End session early
    const endSession = () => {
        setSessionActive(false);
        setQueue([]);
        setCurrentIndex(0);
        setRevealed(false);
        setSessionComplete(false);
        setSessionRatings({ again: 0, hard: 0, good: 0, easy: 0 });
        fetchStats();
    };

    const currentWord = queue[currentIndex];
    const progress = queue.length > 0 ? Math.round(((currentIndex + 1) / queue.length) * 100) : 0;

    // -- Render: Stats & Controls (pre-session) --
    const renderPreSession = () => (
        <>
            {/* Stats Header */}
            <div className="sw-stats-header">
                <div className="sw-stats-left">
                    <div className="sw-stats-row">
                        <div className="sw-stat-item">
                            <span className="sw-stat-label">Total:</span>
                            <span className="sw-stat-value">{stats.total}</span>
                        </div>
                        <div className="sw-stat-item">
                            <span className="sw-stat-label">Reviewed:</span>
                            <span className="sw-stat-value reviewed">{stats.reviewedCount}</span>
                        </div>
                        <div className="sw-stat-item">
                            <span className="sw-stat-label">New:</span>
                            <span className="sw-stat-value not-reviewed">{stats.newCardsTotal || stats.notReviewedCount}</span>
                        </div>
                        <div className="sw-stat-item">
                            <span className="sw-stat-label">Due:</span>
                            <span className="sw-stat-value total-due">{stats.totalDue}</span>
                        </div>
                    </div>
                    {/* Detailed due breakdown */}
                    {(stats.newCardsTotal > 0 || stats.reviewCardsDue > 0) && (
                        <div className="sw-stats-row" style={{ marginTop: 6, fontSize: 13, opacity: 0.8 }}>
                            <span>
                                {stats.newCardsTotal > 0 && (
                                    <span style={{ color: '#ffcc80' }}>{stats.newCardsTotal} new</span>
                                )}
                                {stats.newCardsTotal > 0 && stats.reviewCardsDue > 0 && <span> · </span>}
                                {stats.reviewCardsDue > 0 && (
                                    <span style={{ color: '#66ccff' }}>{stats.reviewCardsDue} review{stats.reviewCardsDue !== 1 ? 's' : ''} due</span>
                                )}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* SRS Settings */}
            <div className="sw-srs-settings">
                <div className="sw-srs-settings-title">SRS Settings</div>
                <div className="sw-srs-settings-row">
                    <div className="sw-srs-setting-group">
                        <label className="sw-srs-setting-label">New cards/day</label>
                        <input
                            type="number"
                            className="sw-srs-setting-input"
                            value={newCardsPerDay}
                            onChange={(e) => setNewCardsPerDay(Math.max(0, parseInt(e.target.value) || 0))}
                            min={0}
                            max={999}
                        />
                    </div>
                    <div className="sw-srs-setting-group">
                        <label className="sw-srs-setting-label">Max reviews/day</label>
                        <input
                            type="number"
                            className="sw-srs-setting-input"
                            value={maxReviewsPerDay}
                            onChange={(e) => setMaxReviewsPerDay(Math.max(1, parseInt(e.target.value) || 1))}
                            min={1}
                            max={9999}
                        />
                    </div>
                    <div className="sw-srs-setting-group sw-srs-toggle-group">
                        <label className="sw-srs-setting-label">New cards ignore review limit</label>
                        <label className="sw-srs-toggle">
                            <input
                                type="checkbox"
                                checked={newCardsIgnoreReviewLimit}
                                onChange={(e) => setNewCardsIgnoreReviewLimit(e.target.checked)}
                            />
                            <span className="sw-srs-toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Start Button */}
            <div className="sw-controls" style={{ marginBottom: 20 }}>
                <button
                    className="sw-start-button"
                    onClick={startSession}
                    disabled={sessionLoading || stats.total === 0}
                >
                    {sessionLoading ? 'Loading...' : 'Start Review'}
                </button>
            </div>
        </>
    );

    // -- Render: Flashcard --
    const renderFlashcard = () => {
        if (!currentWord) return null;

        // Count how many new vs review cards in the queue
        const newCardCount = queue.filter(w => w.isNew).length;
        const reviewCardCount = queue.filter(w => !w.isNew).length;

        return (
            <div className="sw-flashcard-wrapper">
                <div className="sw-flashcard" onClick={handleReveal}>
                    {/* Header with progress */}
                    <div className="sw-flashcard-header">
                        {/* New/Review badge */}
                        <span>
                            {currentWord.isNew ? (
                                <span className="sw-card-type-new">New</span>
                            ) : (
                                <span className="sw-card-type-review">Review</span>
                            )}
                        </span>
                        <div className="sw-progress-container">
                            <div className="sw-progress-bar-bg">
                                <div className="sw-progress-bar-fill" style={{ width: `${progress}%` }} />
                            </div>
                            <span className="sw-card-count">
                                {Math.min(currentIndex + 1, queue.length)} / {queue.length}
                            </span>
                        </div>
                    </div>

                    {/* Word display */}
                    <div className="sw-word-display">
                        {currentWord.item}
                    </div>

                    {/* Reveal hint or definition */}
                    {!revealed && (
                        <div className="sw-reveal-hint">
                            Tap to reveal definition
                        </div>
                    )}

                    {revealed && (
                        <div className="sw-definition-area" onClick={(e) => e.stopPropagation()}>
                            {currentWord.definition ? (
                                <>
                                    <div className="sw-definition-text">{currentWord.definition}</div>
                                    
                                    {/* Example sentences */}
                                    {Array.isArray(currentWord.exampleSentences) && currentWord.exampleSentences.length > 0 && (
                                        <div className="sw-examples-section">
                                            <div className="sw-examples-title">Examples:</div>
                                            {currentWord.exampleSentences.map((example, idx) => (
                                                <div key={idx} className="sw-example-item">
                                                    <div className="sw-example-sentence">{example.sentence}</div>
                                                    <div className="sw-example-translation">{example.translation}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="sw-definition-text" style={{ color: 'rgba(255,255,255,0.5)' }}>
                                    No definition available
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Rating buttons (only visible after reveal) */}
                {revealed && (
                    <div className="sw-rating-row">
                        <button
                            className="sw-rating-btn sw-rating-again"
                            onClick={() => handleRating('again')}
                            title="Repeat in 1 minute"
                        >
                            Again
                        </button>
                        <button
                            className="sw-rating-btn sw-rating-hard"
                            onClick={() => handleRating('hard')}
                        >
                            Hard
                        </button>
                        <button
                            className="sw-rating-btn sw-rating-good"
                            onClick={() => handleRating('good')}
                        >
                            Good
                        </button>
                        <button
                            className="sw-rating-btn sw-rating-easy"
                            onClick={() => handleRating('easy')}
                        >
                            Easy
                        </button>
                    </div>
                )}

                {/* Remove Word link (only visible after reveal) */}
                {revealed && (
                    <div className="sw-remove-word-container">
                        <span
                            className="sw-remove-word-link"
                            onClick={(e) => { e.stopPropagation(); handleRemoveWord(); }}
                            title="Mark as known and remove from SRS queue"
                        >
                            Remove Word
                        </span>
                    </div>
                )}

                {/* SRS interval info (ease factor + next review interval) */}
                <div className="sw-stage-indicator">
                    {currentWord.interval > 0 ? (
                        <>
                            <div
                                className="sw-ease-badge"
                                title="Ease factor — determines how fast intervals grow"
                            >
                                Ease: {currentWord.easeFactor?.toFixed(2) || '2.50'}×
                            </div>
                            <div
                                className="sw-interval-badge"
                                title="Time until next review"
                            >
                                Next: {currentWord.intervalDisplay || formatInterval(currentWord.interval)}
                            </div>
                        </>
                    ) : (
                        <span className="sw-interval-new">New word</span>
                    )}
                </div>
            </div>
        );
    };

    // -- Render: Session Summary --
    const renderSummary = () => {
        const newCardsReviewed = queue.filter(w => w.isNew).length;
        const reviewCardsReviewed = queue.filter(w => !w.isNew).length;
        const totalReviewed = queue.length - sessionRatings.again; // Exclude "again" cards still in queue

        return (
        <div className="sw-summary">
            <div className="sw-summary-title">Session Complete!</div>
            <div className="sw-summary-subtitle">
                {queue.length} card{queue.length !== 1 ? 's' : ''} reviewed
                {newCardsReviewed > 0 && reviewCardsReviewed > 0 && (
                    <span style={{ display: 'block', marginTop: 4, fontSize: 13, opacity: 0.7 }}>
                        {newCardsReviewed} new · {reviewCardsReviewed} reviews
                    </span>
                )}
            </div>

            <div className="sw-summary-grid">
                <div className="sw-summary-item">
                    <div className="sw-summary-item-label">Again</div>
                    <div className="sw-summary-item-value again">{sessionRatings.again}</div>
                </div>
                <div className="sw-summary-item">
                    <div className="sw-summary-item-label">Hard</div>
                    <div className="sw-summary-item-value hard">{sessionRatings.hard}</div>
                </div>
                <div className="sw-summary-item">
                    <div className="sw-summary-item-label">Good</div>
                    <div className="sw-summary-item-value good">{sessionRatings.good}</div>
                </div>
                <div className="sw-summary-item">
                    <div className="sw-summary-item-label">Easy</div>
                    <div className="sw-summary-item-value easy">{sessionRatings.easy}</div>
                </div>
            </div>

            <button className="sw-summary-button" onClick={endSession}>
                Done
            </button>
        </div>
        );
    };

    // -- Render: Empty State --
    const renderEmptyState = () => (
        <div className="sw-empty-state">
            <h2>No Saved Words Yet</h2>
            <p>
                Save words from the dictionary while reading sentences, stories, or chatting with Bojee.
                They'll appear here for flashcard review.
            </p>
        </div>
    );

    // -- Main Render --
    return (
        <>
            <div className="page-title">Saved Words</div>
            <div className="saved-words-container">

            {error && (
                <div className="sw-error">
                    {error}
                    <button
                        onClick={() => setError(null)}
                        style={{
                            marginLeft: 12,
                            background: 'transparent',
                            border: '1px solid #e53935',
                            color: '#e53935',
                            borderRadius: 4,
                            padding: '2px 8px',
                            cursor: 'pointer'
                        }}
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {statsLoading ? (
                <div className="sw-loading">Loading...</div>
            ) : stats.total === 0 && !sessionActive ? (
                renderEmptyState()
            ) : !sessionActive ? (
                <>
                    {renderPreSession()}
                    {!sessionComplete && stats.total > 0 && stats.totalDue === 0 && (
                        <div className="sw-empty-state" style={{ padding: '30px 20px' }}>
                            <h2>All Caught Up!</h2>
                            <p>No words are due for review. Come back later or save more words.</p>
                        </div>
                    )}
                </>
            ) : sessionComplete ? (
                renderSummary()
            ) : (
                <>
                    {/* During session, show minimal stats */}
                    <div className="sw-stats-header" style={{ padding: '10px 16px', marginBottom: 16 }}>
                        <div className="sw-stats-row" style={{ gap: 16 }}>
                            <div className="sw-stat-item">
                                <span className="sw-stat-label">Session:</span>
                                <span className="sw-stat-value">{queue.length} cards</span>
                            </div>
                            {stats.newCardsToday > 0 && (
                                <div className="sw-stat-item">
                                    <span className="sw-stat-label">New:</span>
                                    <span className="sw-stat-value not-reviewed">{stats.newCardsToday}</span>
                                </div>
                            )}
                            {stats.reviewCardsToday > 0 && (
                                <div className="sw-stat-item">
                                    <span className="sw-stat-label">Reviews:</span>
                                    <span className="sw-stat-value reviewed">{stats.reviewCardsToday}</span>
                                </div>
                            )}
                            <div className="sw-stat-item">
                                <span className="sw-stat-label">Done:</span>
                                <span className="sw-stat-value reviewed">{currentIndex}</span>
                            </div>
                        </div>
                        <button
                            onClick={endSession}
                            style={{
                                background: 'rgba(255,255,255,0.15)',
                                border: 'none',
                                color: '#fff',
                                padding: '6px 14px',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 13,
                                fontFamily: 'Nunito, sans-serif'
                            }}
                        >
                            End Session
                        </button>
                    </div>

                    {renderFlashcard()}
                </>
            )}
            </div>
        </>
    );
};

export default SavedWordsFlashcards;
